// A whole flight plan's figures from its stored inputs: the navigation
// log, the fuel plan and the weight & balance. The API returns these with
// the plan; the web app recomputes them with this same function as the
// pilot edits, offline if need be, so the two never disagree.

import type { FuelType } from './constants'
import { fuelPlan, litresToUsg, type FuelPlan } from './fuelPlan'
import { computeLegs, planTotals, plannedCruiseKt, type Leg, type PlanPoint, type PlanTotals, type SpeedUnit } from './navigation'
import type { LoadDataInput } from './plan'
import { computeWeightBalance, taxiFuelUsg, type BaggageArea, type EnvelopePoint, type WeightBalance } from './weightBalance'

// What the planner needs to know about the aircraft on the plan.
export interface PlanAircraftFigures {
  registration: string
  aircraftType: string | null
  fuelType: FuelType
  mtowKg: number | null
  fuelConsumptionUsg: number | null
  // The ICAO flight plan's items 10 and 19.
  equipment: string | null
  transponderMode: string | null
  pbn?: string[]
  emergencyRadio: string | null
  colors: string | null
  /** flight.aircraft_performance; null when none has been entered. */
  performance: {
    emptyWeightKg: number
    unusableFuelUsg: number
    speedUnit: SpeedUnit
    cruiseSpeed: number
    manualCruiseSpeed: number | null
    armBasicMass: number
    armFrontPax: number
    armRearPax: number
    armCargo: number
    armFuelMain: number
    armFuelRes: number
    cgEnvelope: EnvelopePoint[]
    /** Admin-set taxi time before take-off, minutes. */
    taxiMinutes?: number
    /** Fuel burned per 10 minutes of taxi, litres. */
    taxiLitresPerTenMin?: number
    hasRearSeats?: boolean
    baggageAreas?: BaggageArea[]
    baggageMaxTotalKg?: number | null
    maxFuelMainUsg?: number
    maxFuelResUsg?: number
    /** The flight manual's own wording ("78/105"), for the pilot log sheet. */
    stallSpeedFlaps?: string | null
    stallSpeedClean?: string | null
    approachSpeed?: string | null
    bestGlideSpeed?: string | null
    bestClimbSpeed?: string | null
    steepestClimbSpeed?: string | null
  } | null
}

export interface FlightPlanInputs {
  legs: PlanPoint[]
  aircraft: PlanAircraftFigures | null
  load: LoadDataInput
  /** The member's own cruise speed for this aircraft, in its speed unit. */
  memberCruise: number | null
  reserveMinutes?: number
  /** A local flight's planned time, which stands in for the route's: it has no legs to time. */
  localMinutes?: number | null
}

export interface FlightPlanFigures {
  cruiseKt: number | null
  legs: Leg[]
  totals: PlanTotals
  /** null without a fuel burn figure for the aircraft. */
  fuel: FuelPlan | null
  /** null without performance data for the aircraft. */
  weightBalance: WeightBalance | null
}

/** Taxi time assumed where the aircraft has none on file. */
export const DEFAULT_TAXI_MINUTES = 10

/** Taxi fuel assumed where the aircraft has none on file, litres per 10 minutes. */
export const DEFAULT_TAXI_LITRES_PER_10_MIN = 1

export function computeFlightPlan(inputs: FlightPlanInputs): FlightPlanFigures {
  const { aircraft, load } = inputs
  const performance = aircraft?.performance ?? null
  const cruiseKt = performance
    ? plannedCruiseKt({
        speedUnit: performance.speedUnit,
        cruiseSpeed: performance.cruiseSpeed,
        manualCruiseSpeed: performance.manualCruiseSpeed,
        memberOverride: inputs.memberCruise,
      })
    : null

  const legs = computeLegs(inputs.legs, cruiseKt)
  const routeTotals = planTotals(legs)
  const totals = inputs.localMinutes ? { ...routeTotals, minutes: inputs.localMinutes } : routeTotals
  const onBoard = load.fuelMain + load.fuelRes
  const fuel =
    aircraft?.fuelConsumptionUsg != null
      ? fuelPlan({
          burnUsgPerHour: aircraft.fuelConsumptionUsg,
          tripMinutes: totals.minutes,
          reserveMinutes: inputs.reserveMinutes,
          fuelOnBoardUsg: load.fuelUnit === 'l' ? litresToUsg(onBoard) : onBoard,
          unusableUsg: performance?.unusableFuelUsg ?? 0,
        })
      : null

  const weightBalance =
    aircraft && performance
      ? computeWeightBalance(
          { ...performance, fuelType: aircraft.fuelType, mtowKg: aircraft.mtowKg },
          {
            frontKg: load.frontKg,
            rearKg: load.rearKg,
            cargoKg: load.cargoKg,
            baggageKg: load.baggageKg,
            fuelMain: load.fuelMain,
            fuelRes: load.fuelRes,
            fuelUnit: load.fuelUnit,
          },
          fuel?.tripUsg ?? 0,
          taxiFuelUsg(
            performance.taxiLitresPerTenMin ?? DEFAULT_TAXI_LITRES_PER_10_MIN,
            performance.taxiMinutes ?? DEFAULT_TAXI_MINUTES,
          ),
        )
      : null

  return { cruiseKt, legs, totals, fuel, weightBalance }
}
